import React from 'react'
import PropTypes from 'prop-types'
import querystring from 'querystring'
import { useQuery } from '@apollo/client'
import { connect } from 'react-redux'
import uuidGenerator from 'uuid/v4'
import Pagination from 'components/styled-pagination'
import CtaButton from 'components/cta-button'
import Footer from 'components/footer'
import { AdvertiserDisclosure } from 'components/advertiser-disclosure'
import Font from '@cred/react-font'
import Spinner from 'credit-card/card-table/spinner'
import GET_MINI_MARKETPLACE_WIDGET_CARDS from './query'
import CardTile from './card-tile'
import { getPageStats } from '../../../multi-vertical/rate-table/utils'
import useCardsPerPage from './hooks/useCardsPerPage'
import styles from './styles.scss'

const MINI_MARKETPLACE_PAGINATOR = 'mini-marketplace-paginator',
  getAllCardsLink = utms => `/credit-cards/all-credit-cards?${querystring.encode(utms)}`

const MiniMarketplace = ({ page, analyticsContexts, utms, theme }) => {
  const cardsPerPage = useCardsPerPage(),
    { loading, error, data } = useQuery(GET_MINI_MARKETPLACE_WIDGET_CARDS)

  if (loading) return <Spinner />

  if (error) {
    // eslint-disable-next-line
    console.error('Error loading cards: ', error.toString())
    return null
  }

  const creditCards = (data && data.miniMarketplaceWidgetCards) || [],
    tableType = 'shortened',
    { pageCount, pageFrom, pageTo } = getPageStats(tableType, creditCards, page, cardsPerPage),
    // Because we will always need to render a 'View All Cards' button
    // and it takes up the space of one card tile, we will need to update the total page
    // count to be one more than is calculated from the creditCards array when
    // each page is completely filled with card tiles.
    pageCountIncludingViewAllButton = pageCount % cardsPerPage === 0 ? pageCount + 1 : pageCount

  return (
    <div className={styles.miniMarketplaceWidgetRoot}>
      <AdvertiserDisclosure tooltipId="mini-marketplace-advertiser-disclosure-tooltip" theme={theme} />
      <div className={styles.miniMarketplaceWidgetApp}>
        <section className={styles.headerSection}>
          <Font
            element="h2"
            color="accent2"
            type="heading"
            weight="bold"
          >
        Featured credit cards
          </Font>
          <Font
            element="p"
            color="lighter"
            type="disclaimer"
            className={styles.disclaimerText}
          >
        Partner Offers. Terms Apply.
          </Font>
        </section>
        <div className={styles.cardsWrapper}>
          { creditCards
            .slice(pageFrom, pageTo)
            .map(card => (
              <CardTile
                uuid={uuidGenerator()}
                utms={utms}
                analyticsContexts={analyticsContexts}
                key={card.slug}
                {...card}
              />
            ))}
          {/* `page` is zero indexed while pageCount starts at 1, therefore the last page is when `page` + 1
            is equal to `pageCountIncludingViewAllButton` */}
          { pageCountIncludingViewAllButton === (page + 1)
          && (
            <CtaButton
              className={styles.viewAllButton}
              href={getAllCardsLink(utms)}
              transparent
              blank
              analyticsContexts={analyticsContexts}
            >
              View All Cards
            </CtaButton>
          ) }
        </div>
        <Pagination
          pageCount={pageCountIncludingViewAllButton}
          name={MINI_MARKETPLACE_PAGINATOR}
        />
        <Footer
          multiVertical
          containerClass={styles.footerContainer}
        />
      </div>
    </div>
  )
}

const mapStateToProps = ({ components }) => ({
  page: components.pagination[MINI_MARKETPLACE_PAGINATOR],
})

MiniMarketplace.propTypes = {
  page: PropTypes.number,
  analyticsContexts: PropTypes.array,
  utms: PropTypes.object,
  theme: PropTypes.string,
}

export { MiniMarketplace }

export default connect(mapStateToProps)(MiniMarketplace)
